import * as z from 'zod';

import { arrayOfAll, type MandateProps } from '@gitbook/util-ts';

import type { Edits } from '../utils';
import type { GitCommitState, GitFileState } from './git';

export enum RevisionType {
    /** Revision created by editing the content */
    Edits = 'edits',
    /** Revision created as a rollback of a previous revision */
    Rollback = 'rollback',
    /** Revision created when merging a change request */
    Merge = 'merge',
    /** Revision created when updating a change request with changes from primary */
    Update = 'update',
    /** Virtual revision created from a computation */
    Computed = 'computed',
}

export enum RevisionPageType {
    Document = 'document',
    Group = 'group',
    Link = 'link',
    Computed = 'computed',
}

export interface RevisionPages {
    [pageKey: string]: RevisionPage;
}

export interface RevisionReusableContents {
    [contentKey: string]: RevisionReusableContent;
}

/**
 * A Revision is a snapshot of a content at a given time.
 */
export interface Revision extends RevisionInfo {
    files: RevisionFiles;
    rootPages: string[];
    pages: RevisionPages;
    reusableContents: RevisionReusableContents;
    variables?: RevisionVariables;
    conflicts?: RevisionConflicts;
}

// biome-ignore lint/suspicious/noEmptyInterface: We need to support extends here
export interface RevisionConfig {}

export interface DefaultComputedContentSource {
    /**
     * The generator used to compute the content.
     */
    type: string;

    /**
     * Properties passed to the integration for computing the content.
     */
    props: Record<string, any>;

    /**
     * Dependencies the source computation depends on.
     */
    dependencies?: Record<string, { ref: any; value?: any }>;
}

/**
 * As dependencies are optional, we make sure that we generate a type where we can test the dependencies property.
 */
type ComputedContentSourceWithDependencies<Source> = Source extends {
    dependencies?: DefaultComputedContentSource['dependencies'];
}
    ? Source
    : { dependencies?: undefined } & Source;

/**
 * Source for a computed content revision.
 * It represents what will compute the content to be represented in the revision.
 */
export type ComputedContentSourceRevision = RevisionConfig extends {
    computedContentSourceRevision: infer Source;
}
    ? ComputedContentSourceWithDependencies<Source>
    : DefaultComputedContentSource;

/**
 * Source for a computed content document.
 * It represents what will compute the content to be represented in the revision.
 */
export type ComputedContentSourceDocument = RevisionConfig extends {
    computedContentSourceDocument: infer Source;
}
    ? ComputedContentSourceWithDependencies<Source>
    : DefaultComputedContentSource;

export type ComputedContentSource = ComputedContentSourceRevision | ComputedContentSourceDocument;

/**
 * A Revision with conflicts.
 */
export interface ConflictRevision extends MandateProps<Revision, 'conflicts'> {}

/**
 * A Revision resulting from a merge.
 */
export interface MergeRevision extends MandateProps<Revision, 'mergedFrom'> {
    type: RevisionType.Merge;
}

/**
 * A Revision resulting from a rollback.
 */
export interface RollbackRevision extends MandateProps<Revision, 'rolledBackBy'> {
    type: RevisionType.Rollback;
}

/**
 * Type assertion for merge revisions.
 */
export function isMergeRevision(revision: RevisionInfo): revision is MergeRevision {
    return revision.type === RevisionType.Merge && !!revision.mergedFrom;
}

/**
 * Type assertion for merge revisions.
 * Type assertion for rollback revisions.
 */
export function isRollbackRevision(revision: RevisionInfo): revision is RollbackRevision {
    return revision.type === RevisionType.Rollback && !!revision.rolledBackBy;
}

/**
 * Information about a Revision.
 */
export interface RevisionInfo extends RevisionMetadata {
    id: string;
    type: RevisionType;
    edits: Edits;

    /**
     * ID of the content the revision was created under.
     */
    contentId: string;

    /**
     * IDs of parent revisions.
     */
    parents: string[];

    /**
     * ID of the branch the merge revision was created from.
     * The property is only defined when the revision is of type 'merge'.
     */
    mergedFrom?: string;

    /**
     * ID of the user that rollback the revision.
     * The property is only defined when the revision is of type 'rollback'.
     */
    rolledBackBy?: string;

    /**
     * Version of the schema used to normalize the revision last.
     */
    schemaVersion?: number;
}

export const REVISION_INFO_PROPERTIES = arrayOfAll<keyof RevisionInfo>()([
    'id',
    'type',
    'edits',
    'contentId',
    'parents',
    'mergedFrom',
    'rolledBackBy',
    'git',
    'schemaVersion',
]);

/**
 * Mutable metadata of a Revision.
 */
export interface RevisionMetadata {
    /**
     * The Git state, if this Revision is linked to Git.
     */
    git?: RevisionGitState;
}

export interface RevisionFiles {
    [id: string]: RevisionFile;
}

export interface RevisionFileImageDimensions {
    height: number;
    width: number;
}

/**
 * Metadata stored on the blob of a file.
 */
export interface RevisionFileBlobMetadata {
    name: string;
    size: number;
    contentType: string;
}

/**
 * Mutable metadata of a file.
 */
export interface RevisionFileMetadata {
    /**
     * Git state of the File when associated with a Git blob.
     */
    git?: GitFileState;
}

export interface RevisionFile extends RevisionFileBlobMetadata, RevisionFileMetadata {
    id: string;
    edits: Edits;

    downloadURL: string;

    /**
     * ID of the blob in the bucket, when the file was uploaded using Hive.
     * It should NOT be used as a source of truth to download the file (downloadURL should be used instead).
     *
     * The blobId is used for certain operations like duplicating content.
     */
    blobId?: string;

    /**
     * Dimension of the image, if the file is an image.
     */
    dimensions?: RevisionFileImageDimensions;
}

/**
 * References are generic links in content that points to internal or external elements.
 */
export type ContentReference<T extends string = string, P extends Record<string, string> = any> = {
    kind: T;
} & P;

/**
 * The core layout presets for a page
 */
export enum PageLayoutPreset {
    Docs = 'docs',
    Editorial = 'editorial',
    Landing = 'landing',
}

export enum PageLayoutOptionsCoverSize {
    Hero = 'hero',
    Full = 'full',
}

export enum PageLayoutOptionsWidth {
    Default = 'default',
    Wide = 'wide',
}

export const PageLayoutOptionsSchema = z.object({
    /** Should the cover be visible? */
    cover: z.boolean().optional(),
    /** Size of the cover image */
    coverSize: z
        .enum(PageLayoutOptionsCoverSize)
        .refine((val) => typeof val === 'string')
        .optional(),
    /** Width of the page */
    width: z.enum(PageLayoutOptionsWidth).optional(),
    /** Should the title be visible? */
    title: z.boolean().optional(),
    /** Should the description be visible? */
    description: z.boolean().optional(),
    /** Should the table of contents be visible? */
    tableOfContents: z.boolean().optional(),
    /** Should the outline be visible? */
    outline: z.boolean().optional(),
    /** Should the pagination be visible? */
    pagination: z.boolean().optional(),
    /** Should the page metadata section be visible? */
    metadata: z.boolean().optional(),
});

export type PageLayoutOptions = z.infer<typeof PageLayoutOptionsSchema>;
export type PageLayoutOptionsWithDefaults = MandateProps<
    PageLayoutOptions,
    keyof PageLayoutOptions
>;

/**
 * Layout for a page. Either a predefined layout preset or a
 * custom one using explicit options.
 */
export type PageLayout = PageLayoutPreset | PageLayoutOptions;

export type RevisionPage =
    | RevisionPageDocument
    | RevisionPageGroup
    | RevisionPageLink
    | RevisionPageComputed;
export type RevisionPageParent = RevisionPageDocument | RevisionPageGroup;

/**
 * Mutable metadata of a Page.
 */
export interface RevisionPageMetadata {
    /**
     * Git state of the Page when associated with a Git blob.
     */
    git?: GitFileState;
}

export type RevisionPageIconProps = {
    /** UTF-8 code of the emoji. */
    emoji?: string;
    /** Icon name from @gitbook/icons */
    icon?: string;
};

export type RevisionPageBase = RevisionPageMetadata &
    RevisionPageIconProps & {
        id: string;
        title: string;
        edits: Edits;

        /**
         * A conditional expression used to evaluate whether the page should be visible to a user.
         * This expression should evaluate to a boolean.
         */
        condition?: string;
    };

export interface RevisionPageDocumentCover {
    /** Content reference pointing to the source image. */
    ref?: ContentReference<string>;

    /** Content reference pointing to the source image (in dark mode). */
    refDark?: ContentReference<string>;

    /** Vertical position of the cover image. */
    yPos?: number;

    /** Height of the cover image in pixels. */
    height?: number;
}

export type RevisionPageHideable = {
    /**
     * Should the page be hidden from navigation, only accessible by search or directly link.
     * @default false
     */
    hidden?: boolean;

    /**
     * Disable the indexation of the page in the documentation search / AI ask.
     * @default false
     */
    noIndex?: boolean;

    /**
     * Disable the indexation of the page by search engines (Google, Bing, etc.).
     * It'll be automatically considered as `true` if `noIndex` is `true`.
     * @default false
     */
    noRobotsIndex?: boolean;
};

/**
 * A computed page is a page where its content (subpages, document) are dynamically generated
 * from a source.
 */
export type RevisionPageComputed = RevisionPageBase & {
    type: RevisionPageType.Computed;
    computed: ComputedContentSourceRevision;
};

export type RevisionDocumentComputedSource = {
    source: ComputedContentSourceDocument;
    seed: string;
};

export type RevisionPageDocument = RevisionPageBase &
    RevisionPageHideable & {
        type: RevisionPageType.Document;

        slug?: string;
        description?: string;

        /**
         * The properties of the page's cover image.
         */
        cover?: RevisionPageDocumentCover;

        /**
         * Layout of the page. Either a predefined layout preset or a custom one using explicit options.
         * @default 'docs'
         */
        layout?: PageLayout;

        /**
         * ID of the document content for this page.
         */
        document?: string;

        /**
         * Source to compute the document content.
         * The property is only defined when the revision is of type 'computed'.
         */
        computed?: RevisionDocumentComputedSource;

        /**
         * IDs of the children pages.
         */
        pages: string[];

        /**
         * Variables set on the page.
         */
        variables?: RevisionVariables;
    };

export type RevisionPageGroup = RevisionPageBase &
    RevisionPageHideable & {
        type: RevisionPageType.Group;

        slug?: string;
        pages: string[];
    };

export type RevisionPageLink = RevisionPageBase & {
    type: RevisionPageType.Link;
    target: ContentReference;
};

/**
 * Mutable metadata of a Reusable Content.
 */
export type RevisionReusableContentMetadata = {
    /**
     * Git state of the RUC when associated with a Git blob.
     */
    git?: GitFileState;
};

/**
 * Represents reusable content that can be included in multiple pages.
 */
export type RevisionReusableContent = RevisionReusableContentMetadata & {
    /** ID of this reusable content, unique in the revision. */
    id: string;

    /** The ID of the document containing the content. It's optional to allow us to update the revision before the document is created. */
    document?: string;

    /** A title for this reusable content, used for searching. */
    title: string;

    /** A history of edits to the reusable content. */
    edits: Edits;

    /**
     * Source to compute the document content.
     * The property is only defined when the revision is of type 'computed'.
     */
    computed?: RevisionDocumentComputedSource;
};

export enum RevisionConflictState {
    DocumentConflict = 'document-conflict',
    DeletedInBase = 'deleted-in-base',
    DeletedInHead = 'deleted-in-head',
    DeletedInBaseSubtreeModifiedInHead = 'deleted-in-base-subtree-modified-in-head',
    DeletedInHeadSubtreeModifiedInBase = 'deleted-in-head-subtree-modified-in-base',
}

/** State when the revision has conflicts to handle */
export interface RevisionConflicts {
    /** Map of all pages with their conflict state */
    pages: { [pageKey: string]: false | RevisionConflictState };
}

export function assertPageWithDescendants(
    page: RevisionPage | undefined
): asserts page is RevisionPageParent {
    if (
        !page ||
        (page.type !== RevisionPageType.Document && page.type !== RevisionPageType.Group)
    ) {
        throw new Error(`Expected page with descendants, got ${page?.type}`);
    }
}

export function asPageWithDescendants(page: RevisionPage | undefined): RevisionPageParent {
    assertPageWithDescendants(page);
    return page;
}

export function assertPageDocument(
    page: RevisionPage | undefined
): asserts page is RevisionPageDocument {
    if (!page || page.type !== RevisionPageType.Document) {
        throw new Error(`Expected page document, got ${page?.type}`);
    }
}

export function asPageDocument(page: RevisionPage | undefined): RevisionPageDocument {
    assertPageDocument(page);
    return page;
}

export function assertPageLink(page: RevisionPage | undefined): asserts page is RevisionPageLink {
    if (!page || page.type !== RevisionPageType.Link) {
        throw new Error(`Expected page link, got ${page?.type}`);
    }
}

export function assertPageGroup(page: RevisionPage | undefined): asserts page is RevisionPageGroup {
    if (!page || page.type !== RevisionPageType.Group) {
        throw new Error(`Expected page group, got ${page?.type}`);
    }
}

export function assertPageComputed(
    page: RevisionPage | undefined
): asserts page is RevisionPageComputed {
    if (!page || page.type !== RevisionPageType.Computed) {
        throw new Error(`Expected page computed, got ${page?.type}`);
    }
}

/**
 * The Git state of a Revision
 */
export interface RevisionGitState {
    /** The state of the matching Git commit, if it exists */
    commit?: GitCommitState;
}

/**
 * A semantic change in a Revision
 */
export enum RevisionSemanticChangeType {
    PageCreated = 'page_created',
    PageEdited = 'page_edited',
    PageDeleted = 'page_deleted',
    PageMoved = 'page_moved',
    FileCreated = 'file_created',
    FileEdited = 'file_edited',
    FileDeleted = 'file_deleted',
    VariablesEdited = 'variables_edited',
    ReusableContentCreated = 'reusable_content_created',
    ReusableContentDeleted = 'reusable_content_deleted',
    ReusableContentEdited = 'reusable_content_edited',
}

interface ChangeAttribute<T> {
    before: T;
    after: T;
}

type ChangeAttributes<Obj extends {}, Key extends keyof Obj> = {
    [key in Key]?: ChangeAttribute<Obj[key]>;
};

export type RevisionPageDocumentChangeAttributes = ChangeAttributes<
    RevisionPageDocument,
    | 'title'
    | 'description'
    | 'slug'
    | 'document'
    | 'cover'
    | 'emoji'
    | 'layout'
    | 'variables'
    | 'hidden'
    | 'noIndex'
    | 'noRobotsIndex'
    | 'condition'
>;

export type RevisionPageGroupChangeAttributes = ChangeAttributes<
    RevisionPageGroup,
    'title' | 'emoji' | 'slug' | 'hidden' | 'noIndex' | 'noRobotsIndex' | 'condition'
>;

export type RevisionPageLinkChangeAttributes = ChangeAttributes<
    RevisionPageLink,
    'title' | 'emoji' | 'target' | 'condition'
>;

export type ChangedRevisionPage<Page extends RevisionPage> = Pick<Page, 'id' | 'type' | 'title'> & {
    path: string | undefined;
};

export type ChangedRevisionFile = Pick<RevisionFile, 'id' | 'name' | 'contentType' | 'downloadURL'>;

export type ChangedVariables = {
    [variableName: string]: ChangeAttribute<RevisionVariableValue | undefined>;
};

export type ChangedRevisionReusableContent = Pick<
    RevisionReusableContent,
    'id' | 'title' | 'document'
>;
export type ChangedRevisionReusableContentAttributes = ChangeAttributes<
    RevisionReusableContent,
    'title' | 'document'
>;

type RevisionSemanticChangePageEdited =
    | {
          type: RevisionSemanticChangeType.PageEdited;
          page: ChangedRevisionPage<RevisionPageDocument>;
          attributes: RevisionPageDocumentChangeAttributes;
      }
    | {
          type: RevisionSemanticChangeType.PageEdited;
          page: ChangedRevisionPage<RevisionPageGroup>;
          attributes: RevisionPageGroupChangeAttributes;
      }
    | {
          type: RevisionSemanticChangeType.PageEdited;
          page: ChangedRevisionPage<RevisionPageLink>;
          attributes: RevisionPageLinkChangeAttributes;
      };

export type RevisionFileChangeAttributes = ChangeAttributes<
    RevisionFile,
    'name' | 'downloadURL' | 'size' | 'contentType' | 'dimensions'
>;

export type RevisionSemanticChange =
    | {
          type: RevisionSemanticChangeType.PageCreated;
          page: ChangedRevisionPage<RevisionPage>;
      }
    | RevisionSemanticChangePageEdited
    | {
          type: RevisionSemanticChangeType.PageDeleted;
          page: ChangedRevisionPage<RevisionPage>;
      }
    | {
          type: RevisionSemanticChangeType.PageMoved;
          page: ChangedRevisionPage<RevisionPage>;
          parent: {
              before: ChangedRevisionPage<RevisionPageDocument | RevisionPageGroup> | undefined;
              after: ChangedRevisionPage<RevisionPageDocument | RevisionPageGroup> | undefined;
          };
      }
    | {
          type: RevisionSemanticChangeType.FileCreated;
          file: ChangedRevisionFile;
      }
    | {
          type: RevisionSemanticChangeType.FileEdited;
          file: ChangedRevisionFile;
          attributes: RevisionFileChangeAttributes;
      }
    | {
          type: RevisionSemanticChangeType.FileDeleted;
          file: ChangedRevisionFile;
      }
    | {
          type: RevisionSemanticChangeType.VariablesEdited;
          variables: ChangedVariables;
      }
    | {
          type: RevisionSemanticChangeType.ReusableContentCreated;
          reusableContent: ChangedRevisionReusableContent;
      }
    | {
          type: RevisionSemanticChangeType.ReusableContentDeleted;
          reusableContent: ChangedRevisionReusableContent;
      }
    | {
          type: RevisionSemanticChangeType.ReusableContentEdited;
          reusableContent: ChangedRevisionReusableContent;
          attributes: ChangedRevisionReusableContentAttributes;
      };

/**
 * RevisionVariables stored in a revision and on pages.
 */
export interface RevisionVariables {
    [variableName: string]: RevisionVariableValue;
}

/**
 * Value of a specific variable.
 */
export type RevisionVariableValue = number | string | boolean;
